<template>
  <div class="articles-management">
    <div v-if="viewState === 'list'">
      <div class="page-breadcrumb">
        <span>文章管理</span> <i class="fas fa-chevron-right separator"></i> <span>文章列表</span>
      </div>

      <div class="main-actions">
        <button class="action-btn-primary" data-testid="resource-create" @click="showEditView()">
          <i class="fas fa-feather-alt"></i> 新增文章
        </button>
        <button class="action-btn-secondary" @click="$router.push(adminPath('article-categories'))">
          <i class="fas fa-folder-plus"></i> 管理分類
        </button>
      </div>

      <div v-if="isLoading" class="loading-spinner">
        <p><i class="fas fa-spinner fa-spin"></i> 載入文章中...</p>
      </div>

      <div v-if="!isLoading && articles.length > 0" class="data-table-container">
        <table class="data-table">
          <thead>
          <tr>
            <th>封面</th>
            <th>標題</th>
            <th>摘要</th>
            <th>狀態</th>
            <th>最後更新</th>
            <th>操作</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in articles" :key="item.id" :data-resource-id="item.id">
            <td data-label="封面">
              <img v-if="item.coverImageUrl" :src="item.coverImageUrl" alt="文章封面" class="article-cover-image">
              <div v-else class="image-placeholder"><i class="fas fa-image"></i></div>
            </td>
            <td data-label="標題">{{ item.title }}</td>
            <td data-label="摘要" class="summary-cell">{{ item.summary }}</td>
            <td data-label="狀態">
                        <span :class="['status-badge', item.enabled ? 'enabled' : 'disabled']">
                            {{ item.enabled ? '已發布' : '草稿' }}
                        </span>
            </td>
            <td data-label="最後更新">{{ formatDateTime(item.updatedAt) }}</td>
            <td class="actions-cell">
              <button class="action-btn edit-btn" data-testid="resource-edit" @click="showEditView(item.id)">
                <i class="fas fa-edit"></i> 編輯
              </button>
              <button class="action-btn delete-btn" data-testid="resource-delete" @click="handleDelete(item)">
                <i class="fas fa-trash-alt"></i> 刪除
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoading && articles.length === 0" class="no-data-message">
        <i class="fas fa-ghost"></i> 目前沒有任何文章。
      </div>

      <div class="pagination-controls" v-if="!isLoading && totalItems > 0">
        <div class="page-navigation">
          <button @click="goToPage(1)" :disabled="currentPage === 1" class="page-btn">
            <i class="fas fa-angle-double-left"></i>
          </button>
          <button @click="prevPage" :disabled="currentPage === 1" class="page-btn">
            <i class="fas fa-angle-left"></i>
          </button>
          <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 頁</span>
          <button @click="nextPage" :disabled="currentPage === totalPages || totalPages === 0" class="page-btn">
            <i class="fas fa-angle-right"></i>
          </button>
          <button @click="goToPage(totalPages)" :disabled="currentPage === totalPages || totalPages === 0" class="page-btn">
            <i class="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    </div>

    <ArticleEdit
        v-if="viewState === 'edit'"
        :article-id="selectedArticleId"
        @save-success="handleSaveSuccess"
        @cancel="handleCancel"
    />

    <!-- 分類管理模態框 -->
    <div v-if="showCategoryModal" class="modal-overlay" @click="closeCategoryModal">
      <div class="modal-content category-modal" @click.stop>
        <div class="modal-header">
          <h2><i class="fas fa-folder"></i> 文章分類管理</h2>
          <button @click="closeCategoryModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- 新增分類表單 -->
          <div class="category-form">
            <h3>新增分類</h3>
            <div class="form-row">
              <input v-model="newCategory.name" placeholder="分類名稱" class="form-input" />
              <input v-model="newCategory.description" placeholder="分類描述 (選填)" class="form-input" />
              <input v-model="newCategory.color" type="color" class="color-input" />
              <button @click="addCategory" class="add-btn" :disabled="!newCategory.name">
                <i class="fas fa-plus"></i> 新增
              </button>
            </div>
          </div>

          <!-- 分類列表 -->
          <div class="category-list">
            <h3>現有分類</h3>
            <div v-if="loadingCategories" class="loading-text">
              <i class="fas fa-spinner fa-spin"></i> 載入分類中...
            </div>
            <div v-else-if="categories.length === 0" class="no-data">
              尚無分類
            </div>
            <div v-else class="category-items">
              <div v-for="category in categories" :key="category.id" class="category-item">
                <!-- 正常顯示模式 -->
                <div v-if="editingCategory !== category.id" class="category-info">
                  <span class="category-color" :style="{ backgroundColor: category.color }"></span>
                  <span class="category-name">{{ category.name }}</span>
                  <span class="category-desc">{{ category.description || '無描述' }}</span>
                  <span :class="['status-badge', category.enabled ? 'enabled' : 'disabled']">
                    {{ category.enabled ? '啟用' : '停用' }}
                  </span>
                </div>
                <!-- 編輯模式 -->
                <div v-else class="category-edit-form">
                  <div class="edit-form-row">
                    <input v-model="editCategory.name" placeholder="分類名稱" class="form-input edit-input" />
                    <input v-model="editCategory.description" placeholder="分類描述 (選填)" class="form-input edit-input" />
                    <input v-model="editCategory.color" type="color" class="color-input" />
                  </div>
                  <div class="edit-actions">
                    <button @click="saveEditCategory" class="save-edit-btn" :disabled="!editCategory.name.trim()">
                      <i class="fas fa-check"></i> 保存
                    </button>
                    <button @click="cancelEditCategory" class="cancel-edit-btn">
                      <i class="fas fa-times"></i> 取消
                    </button>
                  </div>
                </div>
                <div v-if="editingCategory !== category.id" class="category-actions">
                  <button @click="startEditCategory(category)" class="edit-btn">
                    <i class="fas fa-edit"></i> 編輯
                  </button>
                  <button @click="toggleCategoryStatus(category)" class="toggle-btn">
                    <i :class="category.enabled ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                    {{ category.enabled ? '停用' : '啟用' }}
                  </button>
                  <button @click="deleteCategory(category)" class="delete-btn">
                    <i class="fas fa-trash"></i> 刪除
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeCategoryModal" class="modal-btn cancel-btn">關閉</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import Swal from '@/utils/chineseSwal';
import 'sweetalert2/dist/sweetalert2.min.css';
import { adminPath } from '@/router/paths';
import ArticleEdit from '@/components/ArticleEdit.vue'; // 引入子組件
import { getArticlePage, deleteArticle } from '@/api/article.api.js';
import { 
  getArticleCategoryPage, 
  createArticleCategory, 
  updateArticleCategory, 
  deleteArticleCategory 
} from '@/api/articleCategory.api.js';

// --- State ---
const articles = ref([]);
const isLoading = ref(true);
const viewState = ref('list'); // 'list' | 'edit'
const selectedArticleId = ref(null);

// Pagination State
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = ref(0);
const totalItems = ref(0);

// Category Management State
const showCategoryModal = ref(false);
const categories = ref([]);
const loadingCategories = ref(false);
const newCategory = ref({
  name: '',
  description: '',
  color: '#3498db',
  enabled: true
});
const editingCategory = ref(null);
const editCategory = ref({
  name: '',
  description: '',
  color: '#3498db',
  enabled: true
});


// --- Functions ---
const fetchData = async () => {
  isLoading.value = true;
  try {
    const pageable = { page: currentPage.value - 1, size: itemsPerPage.value, sort: 'updatedAt,desc' };
    const response = await getArticlePage(pageable.page, pageable.size, pageable.sort);
    articles.value = response.content;
    totalItems.value = response.totalElements;
    totalPages.value = response.totalPages;
  } catch (error) {
    // ... 錯誤處理 ...
  } finally {
    isLoading.value = false;
  }
};

const showEditView = (id = null) => {
  selectedArticleId.value = id;
  viewState.value = 'edit';
};

const handleSaveSuccess = () => {
  // 儲存成功後保持在編輯畫面，不跳轉到列表
  fetchData(); // 重新整理列表數據以便返回時顯示最新數據
};

const handleCancel = () => {
  viewState.value = 'list';
  fetchData();
};

const handleDelete = (article) => {
  Swal.fire({
    title: '確定要刪除嗎？',
    html: `您即將刪除文章：<br><b>「${article.title}」</b>`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: '確定刪除',
    cancelButtonText: '取消',
    customClass: {
      popup: 'swal-dark-theme',
      confirmButton: 'swal-confirm-button-delete',
      cancelButton: 'swal-cancel-button'
    }
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await deleteArticle(article.id);
        Swal.fire({
          title: '已刪除！',
          icon: 'success',
          customClass: { popup: 'swal-dark-theme' }
        });
        fetchData();
      } catch (error) {
        // ... 錯誤處理 ...
      }
    }
  });
};

// ... 分頁與日期格式化函式 ...
const formatDateTime = (dateTime) => {
  if (!dateTime) return '-';
  return new Date(dateTime).toLocaleString('zh-TW', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};
const goToPage = (page) => { currentPage.value = page; };
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++; };
const prevPage = () => { if (currentPage.value > 1) currentPage.value--; };

// --- Category Management Functions ---
const closeCategoryModal = () => {
  showCategoryModal.value = false;
  resetNewCategory();
  resetEditCategory();
};

const resetNewCategory = () => {
  newCategory.value = {
    name: '',
    description: '',
    color: '#3498db',
    enabled: true
  };
};

const resetEditCategory = () => {
  editingCategory.value = null;
  editCategory.value = {
    name: '',
    description: '',
    color: '#3498db',
    enabled: true
  };
};

const fetchCategories = async () => {
  loadingCategories.value = true;
  try {
    const response = await getArticleCategoryPage(0, 100, 'createdAt,desc');
    categories.value = response.content;
  } catch (error) {
    console.error('載入分類失敗:', error);
    Swal.fire({
      title: '載入分類失敗',
      text: error.message,
      icon: 'error',
      customClass: { popup: 'swal-dark-theme' }
    });
  } finally {
    loadingCategories.value = false;
  }
};

const addCategory = async () => {
  if (!newCategory.value.name.trim()) return;

  try {
    await createArticleCategory({
      name: newCategory.value.name.trim(),
      description: newCategory.value.description.trim() || null,
      color: newCategory.value.color,
      enabled: newCategory.value.enabled
    });
    
    await fetchCategories();
    resetNewCategory();
    
    Swal.fire({
      title: '新增成功！',
      text: '分類已成功新增',
      icon: 'success',
      customClass: { popup: 'swal-dark-theme' }
    });
  } catch (error) {
    console.error('新增分類失敗:', error);
    Swal.fire({
      title: '新增分類失敗',
      text: error.message,
      icon: 'error',
      customClass: { popup: 'swal-dark-theme' }
    });
  }
};

const toggleCategoryStatus = async (category) => {
  try {
    await updateArticleCategory(category.id, {
      ...category,
      enabled: !category.enabled
    });
    
    await fetchCategories();
    
    Swal.fire({
      title: '更新成功！',
      text: `分類已${!category.enabled ? '啟用' : '停用'}`,
      icon: 'success',
      customClass: { popup: 'swal-dark-theme' }
    });
  } catch (error) {
    console.error('更新分類失敗:', error);
    Swal.fire({
      title: '更新分類失敗',
      text: error.message,
      icon: 'error',
      customClass: { popup: 'swal-dark-theme' }
    });
  }
};

const deleteCategory = (category) => {
  Swal.fire({
    title: '確定要刪除嗎？',
    html: `您即將刪除分類：<br><b>「${category.name}」</b>`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: '確定刪除',
    cancelButtonText: '取消',
    customClass: {
      popup: 'swal-dark-theme',
      confirmButton: 'swal-confirm-button-delete',
      cancelButton: 'swal-cancel-button'
    }
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await deleteArticleCategory(category.id);
        await fetchCategories();
        Swal.fire({
          title: '已刪除！',
          text: '分類已成功刪除',
          icon: 'success',
          customClass: { popup: 'swal-dark-theme' }
        });
      } catch (error) {
        console.error('刪除分類失敗:', error);
        Swal.fire({
          title: '刪除分類失敗',
          text: error.message,
          icon: 'error',
          customClass: { popup: 'swal-dark-theme' }
        });
      }
    }
  });
};

// 開始編輯分類
const startEditCategory = (category) => {
  editingCategory.value = category.id;
  editCategory.value = {
    name: category.name,
    description: category.description || '',
    color: category.color,
    enabled: category.enabled
  };
};

// 取消編輯分類
const cancelEditCategory = () => {
  resetEditCategory();
};

// 保存編輯的分類
const saveEditCategory = async () => {
  if (!editCategory.value.name.trim()) return;
  
  try {
    await updateArticleCategory(editingCategory.value, {
      name: editCategory.value.name.trim(),
      description: editCategory.value.description.trim() || null,
      color: editCategory.value.color,
      enabled: editCategory.value.enabled
    });
    
    await fetchCategories();
    resetEditCategory();
    
    Swal.fire({
      title: '更新成功！',
      text: '分類已成功更新',
      icon: 'success',
      customClass: { popup: 'swal-dark-theme' }
    });
  } catch (error) {
    console.error('更新分類失敗:', error);
    Swal.fire({
      title: '更新分類失敗',
      text: error.message,
      icon: 'error',
      customClass: { popup: 'swal-dark-theme' }
    });
  }
};

// --- Hooks ---
onMounted(fetchData);
watch([currentPage, itemsPerPage], fetchData, { immediate: false });
</script>

<style scoped>
/* SweetAlert2 Dark Theme */
:global(.swal-dark-theme) {
  background-color: rgba(30, 30, 40, 0.98) !important;
  color: #e0e0e0 !important;
  border: 1px solid rgba(160, 100, 220, 0.6) !important;
  border-radius: 8px !important;
  z-index: 10001 !important;
}
:global(.swal2-container) {
  z-index: 10001 !important;
}
:global(.swal-dark-theme .swal2-title) { color: #ff8c69 !important; }
:global(.swal-dark-theme .swal2-html-container) { color: #c0c0c0 !important; }
:global(.swal-dark-theme .swal2-icon.swal2-warning) {
  border-color: #ffc107 !important;
  color: #ffc107 !important;
}
:global(.swal-dark-theme button) {
  border-radius: 5px !important;
  font-weight: 500 !important;
  padding: 10px 20px !important;
  transition: all 0.2s ease !important;
}
:global(.swal-dark-theme .swal-confirm-button-delete) { background-color: #ff5555 !important; }
:global(.swal-dark-theme .swal-confirm-button-delete:hover) { background-color: #ff7070 !important; }
:global(.swal-dark-theme .swal2-confirm) {
  background-color: #50fa7b !important;
  color: #1a1a1a !important;
}
:global(.swal-dark-theme .swal2-cancel) { background-color: rgba(80, 80, 90, 0.7) !important; }
:global(.swal-dark-theme .swal2-cancel:hover) { background-color: rgba(90, 90, 100, 0.8) !important; }

/* --- Component Styles --- */
.articles-management {
  padding: 20px;
  color: #c0c0c0;
}
.page-breadcrumb {
  font-size: 0.95em;
  color: #a0a0b0;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
}
.page-breadcrumb .separator { margin: 0 8px; font-size: 0.7em; }

.main-actions {
  margin-bottom: 25px;
}

.action-btn-primary {
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1em;
  transition: all 0.2s ease;
  background: linear-gradient(145deg, #ff8c69, #e06c55);
  color: #ffffff;
  border: none;
}
.action-btn-primary:hover {
  background: linear-gradient(145deg, #ff9d80, #e67c68);
  box-shadow: 0 2px 8px rgba(255, 140, 105, 0.3);
}
.action-btn-primary i { margin-right: 8px; }

.action-btn-secondary {
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1em;
  transition: all 0.2s ease;
  background: linear-gradient(145deg, #8be9fd, #7acaed);
  color: #1a1a1a;
  border: none;
  margin-left: 10px;
}
.action-btn-secondary:hover {
  background: linear-gradient(145deg, #9defff, #8bd5f7);
  box-shadow: 0 2px 8px rgba(139, 233, 253, 0.3);
}
.action-btn-secondary i { margin-right: 8px; }

.loading-spinner, .no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 1.1em;
  color: #888e95;
  background-color: rgba(30, 30, 40, 0.7);
  border: 1px solid rgba(160, 100, 220, 0.3);
  border-radius: 8px;
}
.no-data-message i { margin-right: 10px; }

.data-table-container {
  background-color: rgba(30, 30, 40, 0.7);
  border: 1px solid rgba(160, 100, 220, 0.3);
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.3);
  overflow-x: auto;
  margin-bottom: 30px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  color: #c0c0c0;
}
.data-table th, .data-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid rgba(160, 100, 220, 0.15);
  vertical-align: middle;
}
.data-table th {
  background-color: rgba(40, 40, 55, 0.8);
  font-weight: 600;
  color: #e0e0e0;
  font-size: 0.9em;
  text-transform: uppercase;
  white-space: nowrap;
}
.data-table tr:last-child td { border-bottom: none; }
.data-table tr:hover td { background-color: rgba(50, 50, 65, 0.7); }

.article-cover-image {
  width: 100px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  background-color: #2c2c3e;
}
.image-placeholder {
  width: 100px;
  height: 60px;
  border-radius: 4px;
  background-color: rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 1.5em;
}

.summary-cell {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.9em;
  color: #a0a0b0;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.85em;
  font-weight: 500;
  white-space: nowrap;
}

.actions-cell { text-align: right; white-space: nowrap; }

.action-btn {
  background: none;
  border: 1px solid transparent;
  color: #adb5bd;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9em;
  margin-left: 8px;
}
.action-btn i { margin-right: 5px; }
.action-btn.edit-btn {
  border-color: rgba(139, 233, 253, 0.5);
  color: #8be9fd;
}
.action-btn.edit-btn:hover { background-color: rgba(139, 233, 253, 0.15); }
.action-btn.delete-btn {
  border-color: rgba(255, 85, 85, 0.5);
  color: #ff5555;
}
.action-btn.delete-btn:hover { background-color: rgba(255, 85, 85, 0.15); }

/* Pagination */
.pagination-controls { display: flex; justify-content: space-between; align-items: center; }

.page-btn {
  background-color: rgba(80, 80, 90, 0.7);
  border: 1px solid rgba(100,100,110,0.8);
  color: #c0c0c0;
  width: 32px; height: 32px;
  border-radius: 4px; cursor: pointer;
}
.page-btn:hover:not(:disabled) { background-color: rgba(90, 90, 100, 0.8); }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-info { font-weight: bold; color: #e0e0e0; }

/* Modal */

.form-group label { display: block; margin-bottom: 8px; color: #a0a0b0; }

.switch input { opacity: 0; width: 0; height: 0; }

.modal-btn i { margin-right: 5px; }

.article-cover-image {
  width: 100px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  background-color: #2c2c3e;
}
.image-placeholder {
  width: 100px;
  height: 60px;
  border-radius: 4px;
  background-color: rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 1.5em;
}
.summary-cell {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.9em;
  color: #a0a0b0;
}

/* 分類管理模態框 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9900;
}

.modal-content {
  background-color: rgba(30, 30, 40, 0.95);
  border: 1px solid rgba(160, 100, 220, 0.4);
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  color: #c0c0c0;
  max-height: 90vh;
  overflow-y: auto;
}

.category-modal {
  width: 800px;
  max-width: 90vw;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid rgba(160, 100, 220, 0.2);
}

.modal-header h2 {
  color: #ff8c69;
  margin: 0;
  font-size: 1.3em;
}

.modal-header h2 i {
  margin-right: 10px;
}

.close-btn {
  background: none;
  border: none;
  color: #adb5bd;
  font-size: 1.2em;
  cursor: pointer;
  padding: 5px;
  border-radius: 3px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  color: #ff5555;
  background-color: rgba(255, 85, 85, 0.1);
}

.modal-body {
  padding: 20px;
}

.category-form {
  background-color: rgba(20, 20, 30, 0.7);
  border: 1px solid rgba(160, 100, 220, 0.2);
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 30px;
}

.category-form h3 {
  color: #e0e0e0;
  margin-bottom: 15px;
  font-size: 1.1em;
}

.form-row {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.form-input {
  padding: 8px 12px;
  border: 1px solid rgba(160, 100, 220, 0.3);
  background-color: rgba(40, 40, 55, 0.8);
  color: #e0e0e0;
  border-radius: 4px;
  font-size: 0.9em;
  flex: 1;
  min-width: 150px;
}

.form-input:focus {
  outline: none;
  border-color: rgba(139, 233, 253, 0.5);
  box-shadow: 0 0 5px rgba(139, 233, 253, 0.3);
}

.color-input {
  width: 50px;
  height: 36px;
  border: 1px solid rgba(160, 100, 220, 0.3);
  border-radius: 4px;
  cursor: pointer;
  background: none;
}

.add-btn {
  padding: 8px 16px;
  background: linear-gradient(145deg, #50fa7b, #45e66b);
  color: #1a1a1a;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.add-btn:hover:not(:disabled) {
  background: linear-gradient(145deg, #5fff85, #4fea75);
  box-shadow: 0 2px 8px rgba(80, 250, 123, 0.3);
}

.add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.category-list h3 {
  color: #e0e0e0;
  margin-bottom: 15px;
  font-size: 1.1em;
}

.loading-text {
  text-align: center;
  color: #888e95;
  padding: 20px;
}

.no-data {
  text-align: center;
  color: #888e95;
  padding: 20px;
  font-style: italic;
}

.category-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background-color: rgba(40, 40, 55, 0.7);
  border: 1px solid rgba(160, 100, 220, 0.2);
  border-radius: 6px;
  transition: all 0.2s ease;
}

.category-item:hover {
  background-color: rgba(50, 50, 65, 0.7);
}

.category-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.category-color {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

.category-name {
  font-weight: 500;
  color: #e0e0e0;
  min-width: 100px;
}

.category-desc {
  color: #a0a0b0;
  font-size: 0.9em;
  flex: 1;
}

.category-actions {
  display: flex;
  gap: 8px;
}

.toggle-btn {
  padding: 6px 12px;
  border: 1px solid rgba(139, 233, 253, 0.5);
  color: #8be9fd;
  background: rgba(139, 233, 253, 0.1);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.85em;
}

.toggle-btn:hover {
  background: rgba(139, 233, 253, 0.2);
}

.delete-btn {
  padding: 6px 12px;
  border: 1px solid rgba(255, 85, 85, 0.5);
  color: #ff5555;
  background: rgba(255, 85, 85, 0.1);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.85em;
}

.delete-btn:hover {
  background: rgba(255, 85, 85, 0.2);
}

.edit-btn {
  padding: 6px 12px;
  border: 1px solid rgba(255, 193, 7, 0.5);
  color: #ffc107;
  background: rgba(255, 193, 7, 0.1);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.85em;
}

.edit-btn:hover {
  background: rgba(255, 193, 7, 0.2);
}

/* 編輯分類表單樣式 */
.category-edit-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 15px;
  background-color: rgba(40, 40, 55, 0.9);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
}

.edit-form-row {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.edit-input {
  padding: 8px 12px;
  border: 1px solid rgba(160, 100, 220, 0.3);
  background-color: rgba(20, 20, 30, 0.8);
  color: #e0e0e0;
  border-radius: 4px;
  font-size: 0.9em;
  flex: 1;
  min-width: 120px;
}

.edit-input:focus {
  outline: none;
  border-color: rgba(255, 193, 7, 0.5);
  box-shadow: 0 0 5px rgba(255, 193, 7, 0.3);
}

.edit-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.save-edit-btn {
  padding: 6px 12px;
  background: linear-gradient(145deg, #50fa7b, #45e66b);
  color: #1a1a1a;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.85em;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.save-edit-btn:hover:not(:disabled) {
  background: linear-gradient(145deg, #5fff85, #4fea75);
  box-shadow: 0 2px 8px rgba(80, 250, 123, 0.3);
}

.save-edit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: rgba(80, 80, 90, 0.5);
  color: #a0a0b0;
}

.cancel-edit-btn {
  padding: 6px 12px;
  border: 1px solid rgba(255, 85, 85, 0.5);
  color: #ff5555;
  background: rgba(255, 85, 85, 0.1);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.85em;
  display: flex;
  align-items: center;
  gap: 4px;
}

.cancel-edit-btn:hover {
  background: rgba(255, 85, 85, 0.2);
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid rgba(160, 100, 220, 0.2);
  display: flex;
  justify-content: flex-end;
}

.modal-btn {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9em;
  transition: all 0.2s ease;
  border: none;
}

.cancel-btn {
  background-color: rgba(80, 80, 90, 0.7);
  color: #c0c0c0;
  border: 1px solid rgba(100, 100, 110, 0.8);
}

.cancel-btn:hover {
  background-color: rgba(90, 90, 100, 0.8);
}

/* 響應式設計 */
@media (max-width: 768px) {
  .category-modal {
    width: 95vw;
    margin: 20px;
  }
  
  .form-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .form-input {
    min-width: auto;
  }
  
  .category-item {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .category-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .category-actions {
    justify-content: flex-end;
  }
  
  .edit-form-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .edit-input {
    min-width: auto;
  }
  
  .edit-actions {
    justify-content: center;
  }
}
</style>
